import { FlowButton } from 'components/ui/flow-button';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Compass, Loader2, LockKeyhole } from 'lucide-react';
import { signInAdmin, signInAdminWithGoogle } from 'lib/adminAuth';

function setLoginMetadata() {
  document.title = 'Admin Login | Magellan';
  let robots = document.head.querySelector('meta[name="robots"]');
  if (!robots) {
    robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    document.head.appendChild(robots);
  }
  robots.setAttribute('content', 'noindex,nofollow');
}

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoginMetadata();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await signInAdmin(form.email, form.password);
      navigate('/admin', { replace: true });
    } catch (submitError) {
      setError(submitError?.message || 'Unable to sign in.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setIsGoogleSubmitting(true);

    try {
      await signInAdminWithGoogle();
      navigate('/admin', { replace: true });
    } catch (submitError) {
      setError(submitError?.message || 'Unable to sign in with Google.');
    } finally {
      setIsGoogleSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas relative overflow-hidden flex items-center justify-center px-6 py-24">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(76,141,204,0.14),transparent_32%),linear-gradient(135deg,rgba(255,255,255,0.05),transparent_40%)]" />
      <div className="absolute inset-0 grain-overlay" />

      <div className="relative z-10 w-full max-w-md border border-gold/20 bg-surface p-8 md:p-10 shadow-[0_30px_100px_rgba(0,0,0,0.45)]">
        <div className="flex items-center gap-3 mb-8">
          <Compass className="w-8 h-8 text-accent-ink" strokeWidth={1.5} />
          <div>
            <p className="font-display text-xl text-ink tracking-[0.3em]">MAGELLAN</p>
            <p className="text-[10px] uppercase tracking-[0.28em] text-accent-ink/70 font-body">Admin</p>
          </div>
        </div>

        <h1 className="font-display text-4xl text-ink tracking-tight mb-4">Admin login</h1>
        <p className="font-body text-copy/45 text-sm leading-relaxed mb-8">
          Sign in with an authorized Magellan admin account.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <FlowButton
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isSubmitting || isGoogleSubmitting}
            className="w-full border border-ink/15 bg-ink/[0.03] text-ink px-8 py-4 text-[11px] uppercase tracking-[0.22em] font-body font-bold hover:border-gold/45  transition-all inline-flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGoogleSubmitting ? (
              <>Opening Google <Loader2 className="w-4 h-4 animate-spin" /></>
            ) : (
              <>
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ink text-[12px] font-bold text-obsidian">G</span>
                Sign in with Google
              </>
            )}
          </FlowButton>

          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-ink/10" />
            <span className="text-[10px] uppercase tracking-[0.24em] text-copy/25 font-body">or</span>
            <div className="h-px flex-1 bg-ink/10" />
          </div>

          <div>
            <label htmlFor="admin-email" className="text-[10px] uppercase tracking-[0.24em] text-copy/35 font-body mb-3 block">Email</label>
            <input
              id="admin-email"
              type="email"
              required
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              className="w-full bg-transparent border-b border-ink/15 text-ink py-3 px-0 focus:border-gold focus:outline-none transition-colors placeholder:text-ink/18 font-body"
              placeholder="admin@example.com"
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="admin-password" className="text-[10px] uppercase tracking-[0.24em] text-copy/35 font-body mb-3 block">Password</label>
            <input
              id="admin-password"
              type="password"
              required
              value={form.password}
              onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
              className="w-full bg-transparent border-b border-ink/15 text-ink py-3 px-0 focus:border-gold focus:outline-none transition-colors placeholder:text-ink/18 font-body"
              placeholder="Password"
              autoComplete="current-password"
            />
          </div>

          {error ? (
            <div role="alert" className="border border-red-400/20 bg-red-500/5 p-4">
              <p className="font-body text-red-200 text-sm leading-relaxed">{error}</p>
            </div>
          ) : null}

          <FlowButton
            type="submit"
            disabled={isSubmitting || isGoogleSubmitting}
            className="w-full   px-8 py-4 text-[11px] uppercase tracking-[0.22em] font-body font-bold  transition-all inline-flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>Checking access <Loader2 className="w-4 h-4 animate-spin" /></>
            ) : (
              <>Sign in <LockKeyhole className="w-4 h-4" /></>
            )}
          </FlowButton>
        </form>
      </div>
    </div>
  );
}
